import { useState, useEffect, useRef } from 'react'
import { t } from '@/components/common/Helpers'
import PhotoCarouselModal from './PhotoCarouselModal'

export default function HorizontalGallery({
    obituary,
    titleClasses = 'app-title text-4xl mb-6 text-center text-gray-700',
    buttonClasses = 'shadow-sm border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 px-6 py-3 text-sm rounded-md inline-flex items-center justify-center font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200',
    fitContainer = false,
    isPreviewMode = false
}) {
    const [showFullGallery, setShowFullGallery] = useState(false)
    const [selectedImageIndex, setSelectedImageIndex] = useState(0)
    const [scrollY, setScrollY] = useState(0)
    const galleryRef = useRef(null)

    useEffect(() => {
        const handleScroll = () => {
            setScrollY(window.scrollY)
        }

        window.addEventListener('scroll', handleScroll, { passive: true })
        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [])

    if (!obituary || !obituary.gallery_photos || obituary.gallery_photos.length === 0) {
        return null
    }

    const photos = obituary.gallery_photos.map((photo, index) => ({
        original: photo.xl,
        thumbnail: photo.md,
        originalAlt: `${obituary.display_name} photo ${index + 1} of ${obituary.gallery_photos.length}`,
        thumbnailAlt: `${obituary.display_name} photo thumbnail ${index + 1} of ${obituary.gallery_photos.length}`,
        thumbnailLoading: "lazy",
        loading: "lazy",
    }))

    // For parallax effect, ensure we have enough photos to span the full width
    let displayPhotos = photos.slice(0, 7)
    const originalDisplayCount = displayPhotos.length

    if (displayPhotos.length < 15) {
        // Duplicate photos to fill the space for better parallax effect
        const duplicatesNeeded = displayPhotos.length * 2
        const duplicatedPhotos = []
        for (let i = 0; i < duplicatesNeeded; i++) {
            const photoToDuplicate = displayPhotos[i % originalDisplayCount]
            duplicatedPhotos.push({
                ...photoToDuplicate
            })
        }
        displayPhotos = [...displayPhotos, ...duplicatedPhotos]
    }

    const handleViewGallery = (imageIndex = 0) => {
        if (isPreviewMode) {
            return
        }
        setSelectedImageIndex(imageIndex)
        setShowFullGallery(true)
    }

    const handlePhotoClick = (photoIndex) => {
        if (isPreviewMode) {
            return
        }
        // If it's a duplicate photo, map back to the original photo index
        const originalPhotoIndex = photoIndex >= originalDisplayCount ? photoIndex % originalDisplayCount : photoIndex
        handleViewGallery(originalPhotoIndex)
    }

    // Calculate parallax transform - start images off-screen left and move right on scroll
    const getParallaxTransform = (index) => {
        if (typeof window === 'undefined') {
            return 'translateX(0px)'
        }

        const parallaxStrength = 0.1 // How much the images move with scroll
        const baseOffset = -window.innerWidth * 0.1 // Start images off-screen to the left
        const scrollOffset = scrollY * parallaxStrength
        return `translateX(${baseOffset + scrollOffset}px)`
    }

    return (
        <>
            <div
                ref={galleryRef}
                className={`relative bg-gray-50 py-12 md:py-16 ${fitContainer
                    ? ''
                    : 'w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw]'
                    }`}
            >

                {!isPreviewMode &&
                    <div className="text-center mb-8 md:mb-12 px-4 md:px-8">
                        <h2 className={titleClasses}>
                            {t.trans('obituaries.photo-gallery-life-of') + ' ' + obituary.first_name}
                        </h2>
                    </div>
                }

                <div className="flex justify-center items-center gap-3 md:gap-4 lg:gap-6 overflow-hidden w-full min-w-full px-4 md:px-8">
                    {displayPhotos.map((photo, index) => (
                        <div
                            key={index}
                            className="group relative flex-shrink-0 overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-all duration-300 bg-white cursor-pointer"
                            style={{
                                width: fitContainer ? 'clamp(120px, 15vw, 200px)' : 'clamp(160px, 20vw, 280px)',
                                height: fitContainer ? 'clamp(160px, 20vw, 267px)' : 'clamp(213px, 26.7vw, 373px)',
                                transform: getParallaxTransform(index),
                                transition: 'transform 0.1s ease-out'
                            }}
                            onClick={isPreviewMode ? () => { } : () => handlePhotoClick(index)}
                        >
                            <img
                                src={photo.thumbnail}
                                alt={`${obituary.display_name} photo ${index + 1}`}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </div>
                    ))}
                </div>

                <div className="text-center mt-8 md:mt-12 px-4 md:px-8">
                    {!isPreviewMode && (
                        <button
                            onClick={() => handleViewGallery(0)}
                            className={buttonClasses}
                        >
                            View All Photos
                        </button>
                    )}
                </div>
            </div>

            <PhotoCarouselModal
                photos={photos}
                isOpen={showFullGallery}
                onClose={() => setShowFullGallery(false)}
                currentPhotoIndex={selectedImageIndex}
                setCurrentPhotoIndex={setSelectedImageIndex}
                showBackgroundBlur={true}
            />
        </>
    )
}
